/**
 * Liest Telemetriezeilen aus dem seriellen Datenstrom (W11, W12).
 *
 * Die Firmware sendet je Messpunkt eine Zeile::
 *
 *     <<T>>{"schema":1,"seq":12,"t":3.21,"v":{"tilt_rad":0.03}}
 *
 * Die Marke am Zeilenanfang trennt sie von gewoehnlichen `print`-Ausgaben.
 * Zeilen ohne Marke gehen unveraendert an die Konsole; sonst verschwaende die
 * Telemetrie die Ausgaben der Studierenden.
 *
 * Der Datenstrom kommt in Stuecken beliebiger Groesse an. Eine Zeile kann
 * mitten in der Marke oder mitten im JSON geteilt sein, deshalb sammelt der
 * Leser unvollstaendige Zeilen und wertet sie erst aus, wenn der Zeilenumbruch
 * da ist.
 */

/** Marke am Zeilenanfang. Muss zu robot/_telemetry.py passen. */
export const MARKER = "<<T>>";

/** Marke der Anmeldung von Bedienelementen. Passt zu robot/_remote.py. */
export const DECLARE_MARKER = "<<D>>";

/** Marke, mit der die Oberflaeche Werte an den Roboter schickt. */
export const COMMAND_MARKER = "<<C>>";

/**
 * Anfrage beim Verbinden: laeuft auf dem Roboter ein Programm?
 *
 * Ein Python-Kommentar, damit die REPL ohne laufendes Programm nichts tut.
 * Muss zu ``HELLO_MARKER`` in python/robot/_remote.py passen.
 */
export const HELLO_LINE = "# <<H>>";

/** Antwort eines laufenden Programms: Geraeteinformation. */
export const INFO_MARKER = "<<I>>";

/** Fassung des Nachrichtenaufbaus, die dieser Leser versteht. */
export const SCHEMA = 1;

/**
 * Wie die Lage gezeigt werden soll - vom Programm bestimmt, mit
 * ``telemetry.send_orientation(plot=..., view=...)``.
 *
 * ``both`` ist die Voreinstellung und steht nicht in der Zeile.
 */
export type OrientationShow = "both" | "plot" | "view" | "none";

export interface TelemetrySample {
  /** Laufende Nummer der Firmware; Luecken bedeuten verworfene Messwerte. */
  sequence: number;
  /** Zeit seit Programmstart auf dem Roboter, in Sekunden. */
  time: number;
  /** Die gemeldeten Werte. `null` heisst: kein gueltiger Messwert. */
  values: Record<string, number | null>;
  /** Anzeigewunsch fuer die Lage, falls die Zeile einen traegt. */
  show?: OrientationShow;
}

/** Ein Bedienelement, das das laufende Programm angemeldet hat. */
export interface RemoteControl {
  name: string;
  kind: "number" | "switch" | "button" | "joystick";
  /** Nur bei "number": Grenzen des Schiebereglers. */
  min?: number;
  max?: number;
  /** Wert beim Anmelden. Bei "joystick" ein Paar `[x, y]`. */
  value?: RemoteValue | null;
}

/**
 * Was ein Bedienelement an den Roboter zurueckschickt.
 *
 * Das Paar gehoert zum Joystick: zwei Achsen, beide von -1 bis 1, **x positiv
 * rechts und y positiv vorwaerts**. Auf dem Bildschirm zeigt y nach unten -
 * die Umrechnung passiert hier und nicht im Schuelerprogramm, damit dort die
 * Richtung steht, die der Roboter faehrt.
 */
export type RemoteValue = number | boolean | [number, number];

export interface ParseResult {
  /** Zeilen ohne Marke, unveraendert fuer die Konsole. */
  text: string;
  /** Erkannte Messpunkte. */
  samples: TelemetrySample[];
  /**
   * Zuletzt angemeldete Bedienelemente, falls in diesem Stueck eine
   * Anmeldung kam. Sie ersetzt die bisherigen vollstaendig: startet ein
   * anderes Programm, sind die alten Bedienelemente gegenstandslos.
   */
  controls?: RemoteControl[];
  /**
   * Antwort eines laufenden Programms auf ``HELLO_LINE``, roh.
   *
   * Roh, weil diese Schicht die Bedeutung nicht kennt; ausgewertet wird sie
   * in der Verbindung, genau wie die Geraeteinformation aus der Raw-REPL.
   */
  info?: unknown;
  /**
   * Die Eingabeaufforderung ``>>> `` steht am Ende: auf dem Roboter laeuft
   * kein Programm mehr.
   */
  prompt?: boolean;
  /** Ein Soft-Reboot hat begonnen; das Programm faengt von vorn an. */
  reboot?: boolean;
  /** Zeilen mit Marke, die sich nicht lesen liessen. */
  malformed: number;
}

/**
 * Zerlegt den Datenstrom in Konsolentext und Messpunkte.
 *
 * Ein Aufruf je Datenstueck; der Leser haelt die unvollstaendige letzte Zeile
 * bis zum naechsten Aufruf fest.
 */
export class TelemetryReader {
  private pending = "";
  private malformed = 0;

  push(chunk: string): ParseResult {
    this.pending += chunk;
    const samples: TelemetrySample[] = [];
    let text = "";
    let malformed = 0;
    let controls: RemoteControl[] | undefined;
    let info: unknown;
    let reboot = false;

    // Der letzte Teil bleibt liegen: ohne Zeilenumbruch ist nicht
    // entscheidbar, ob die Zeile vollstaendig ist.
    const parts = this.pending.split(/\r?\n/);
    this.pending = parts.pop() ?? "";

    for (const line of parts) {
      if (line.startsWith("MPY: soft reboot")) {
        reboot = true;
      }
      if (line.startsWith(INFO_MARKER)) {
        try {
          info = JSON.parse(line.slice(INFO_MARKER.length));
        } catch {
          malformed += 1;
          this.malformed += 1;
        }
        continue;
      }
      if (line.includes(HELLO_LINE)) {
        // Das Echo der eigenen Anfrage. Laeuft kein Programm, gibt die REPL
        // die Zeile als ">>> # <<H>>" zurueck; in der Konsole ist sie nur
        // eine Frage an sich selbst.
        continue;
      }
      if (line.startsWith(DECLARE_MARKER)) {
        const declared = parseControls(line);
        if (declared) {
          controls = declared;
        } else {
          malformed += 1;
          this.malformed += 1;
        }
        continue;
      }
      if (!line.startsWith(MARKER)) {
        text += line + "\n";
        continue;
      }
      const sample = parseSample(line);
      if (sample) {
        samples.push(sample);
      } else {
        malformed += 1;
        this.malformed += 1;
      }
    }
    // Die Eingabeaufforderung hat keinen Zeilenumbruch und bleibt deshalb als
    // unvollstaendige Zeile liegen. Steht dort genau ">>> ", wartet der
    // Roboter auf Eingaben - es laeuft kein Programm.
    const prompt = this.pending === ">>> ";
    return { text, samples, controls, info, prompt, reboot, malformed };
  }

  /**
   * Gibt zurueck, was noch keine vollstaendige Zeile ist.
   *
   * Beim Trennen der Verbindung soll der Rest nicht verloren gehen, sondern in
   * der Konsole erscheinen.
   */
  flush(): string {
    const rest = this.pending;
    this.pending = "";
    return rest.startsWith(MARKER) ||
      rest.startsWith(DECLARE_MARKER) ||
      rest.startsWith(INFO_MARKER) ||
      rest.includes(HELLO_LINE)
      ? ""
      : rest;
  }

  reset(): void {
    this.pending = "";
    this.malformed = 0;
  }

  malformedCount(): number {
    return this.malformed;
  }
}

/** Liest eine einzelne Zeile mit Marke. */
export function parseSample(line: string): TelemetrySample | undefined {
  if (!line.startsWith(MARKER)) {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(line.slice(MARKER.length));
  } catch {
    return undefined;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return undefined;
  }
  const message = parsed as Record<string, unknown>;
  if (message.schema !== SCHEMA) {
    // Eine andere Fassung wird nicht geraten. Der Versionsabgleich beim
    // Verbinden meldet den Konflikt bereits mit einer verstaendlichen
    // Erklaerung (W03).
    return undefined;
  }
  if (typeof message.seq !== "number" || typeof message.t !== "number") {
    return undefined;
  }
  const raw = message.v;
  if (typeof raw !== "object" || raw === null) {
    return undefined;
  }

  const values: Record<string, number | null> = {};
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === null) {
      // Ausdruecklich kein gueltiger Messwert. Bleibt als Luecke erhalten und
      // wird nicht zu null verrechnet (W12, G08).
      values[name] = null;
    } else if (typeof value === "number" && Number.isFinite(value)) {
      values[name] = value;
    } else {
      values[name] = null;
    }
  }
  const show =
    message.show === "plot" || message.show === "view" || message.show === "none"
      ? message.show
      : undefined;
  return { sequence: message.seq, time: message.t, values, show };
}

/** Liest eine Anmeldung von Bedienelementen. */
export function parseControls(line: string): RemoteControl[] | undefined {
  if (!line.startsWith(DECLARE_MARKER)) {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(line.slice(DECLARE_MARKER.length));
  } catch {
    return undefined;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return undefined;
  }
  const message = parsed as Record<string, unknown>;
  if (message.schema !== SCHEMA || !Array.isArray(message.controls)) {
    return undefined;
  }
  const result: RemoteControl[] = [];
  for (const raw of message.controls) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const control = raw as Record<string, unknown>;
    if (typeof control.name !== "string" || !control.name) {
      continue;
    }
    const kind = control.kind;
    if (
      kind !== "number" &&
      kind !== "switch" &&
      kind !== "button" &&
      kind !== "joystick"
    ) {
      // Ein unbekannter Typ bekommt kein Bedienelement. Zu raten waere
      // schlimmer, als ihn wegzulassen.
      continue;
    }
    const entry: RemoteControl = { name: control.name, kind };
    if (kind === "number") {
      if (typeof control.min !== "number" || typeof control.max !== "number") {
        continue;
      }
      entry.min = control.min;
      entry.max = control.max;
    }
    if (
      typeof control.value === "number" ||
      typeof control.value === "boolean"
    ) {
      entry.value = control.value;
    } else if (
      kind === "joystick" &&
      Array.isArray(control.value) &&
      control.value.length >= 2 &&
      typeof control.value[0] === "number" &&
      typeof control.value[1] === "number"
    ) {
      entry.value = [control.value[0], control.value[1]];
    }
    result.push(entry);
  }
  return result;
}

/** Baut die Zeile, mit der ein Wert an den Roboter geht. */
export function formatCommand(values: Record<string, RemoteValue>): string {
  return COMMAND_MARKER + JSON.stringify({ schema: SCHEMA, set: values });
}
